import { CaptionLabel } from '@/components/labels/CaptionLabel';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { AXLE_RED, YELLOW_STATUS } from '@/constants/colors';
import { ExternalIdFragment, existsInExternalSystem } from '@/types/externalId';
import { SyncState } from '@/types/graphql';
import { formatDatetime } from '@/utils/datetimes/formatting';
import { gql } from '@apollo/client';
import SyncProblem from '@mui/icons-material/SyncProblem';
import { Box, Tooltip } from '@mui/material';
import moment from 'moment';

import { SyncStatusDisplayFragment } from './SyncStatusDisplay.gql.types';

export const SyncStatusFragment = gql`
    fragment SyncStatusDisplay on SyncStatus {
        id
        state
        lastAttempt {
            id
            createdAt
            hasError
            userSafeErrorType
            userSafeErrorPayload
        }
        lastSuccess {
            id
            createdAt
        }
        lastFailure {
            id
            createdAt
            userSafeErrorMessage
        }
    }
`;

function getSyncStatusLightStyles(state: SyncState, hasSucceeded: boolean) {
    switch (state) {
        case SyncState.SUCCEEDING:
            return {
                backgroundColor: hasSucceeded ? 'success.main' : '#aaaaaa',
            };
        case SyncState.FAILING:
            return {
                backgroundColor: AXLE_RED,
            };
        case SyncState.PENDING:
            return {
                backgroundColor: YELLOW_STATUS,
            };
        case SyncState.NEVER_ATTEMPTED:
        default:
            return {
                backgroundColor: '#aaaaaa',
            };
    }
}

export const SyncStatusLight = ({
    state,
    hasSucceeded,
}: {
    state: SyncState;
    hasSucceeded: boolean;
}) => {
    return (
        <Box
            sx={{
                width: 8,
                height: 8,
                flexShrink: 0,
                borderRadius: '50%',
                ...getSyncStatusLightStyles(state, hasSucceeded),
            }}
        />
    );
};

export const FailingSyncIcon = ({
    attemptDatetime,
    userSafeErrorMessage = null,
}: {
    attemptDatetime: Date;
    userSafeErrorMessage?: string | null;
}) => {
    const attemptMoment = moment(attemptDatetime);
    const failedToSync = `Failed to sync ${attemptMoment.fromNow()}`;
    return (
        <Tooltip
            title={
                userSafeErrorMessage ? (
                    <VerticalStack>
                        <Box>{failedToSync}</Box>
                        <Box>{userSafeErrorMessage}</Box>
                    </VerticalStack>
                ) : (
                    failedToSync
                )
            }
        >
            <SyncProblem sx={{ color: AXLE_RED }} fontSize='small' />
        </Tooltip>
    );
};

export const SyncStatusDisplay = ({
    syncStatus,
    externalId,
    externalIds,
}: {
    syncStatus: SyncStatusDisplayFragment;
    externalId: string | null;
    externalIds: ExternalIdFragment[] | null;
}) => {
    const resourceExistsInExternalSystem = existsInExternalSystem(externalId, externalIds);
    const { lastAttempt, lastSuccess, state } = syncStatus;
    const light = (
        <SyncStatusLight
            state={state}
            hasSucceeded={!!lastSuccess && resourceExistsInExternalSystem}
        />
    );

    if (!lastAttempt) {
        return (
            <HorizontalStack alignItems='center' spacing={1}>
                {light}
                <CaptionLabel>Sync never attempted</CaptionLabel>
            </HorizontalStack>
        );
    }
    const lastAttemptMoment = moment(lastAttempt.createdAt);
    const lastAttemptTime = formatDatetime(lastAttemptMoment, lastAttemptMoment.tz() ?? null, {
        hideDateWhenToday: true,
    });
    const lastAttemptTimeFull = lastAttemptMoment.format('MMM D, h:mm A z');
    const lastSuccessMoment = lastSuccess ? moment(lastSuccess.createdAt) : null;
    const lastSuccessTimeFull =
        lastSuccessMoment && resourceExistsInExternalSystem
            ? `Last succeeded: ${lastSuccessMoment.format('MMM D, h:mm A z')}`
            : null;
    if (lastAttempt.hasError) {
        return (
            <Tooltip title={lastSuccessTimeFull ?? lastAttemptTimeFull}>
                <Box>
                    <HorizontalStack spacing={1} alignItems='center'>
                        {light}
                        <CaptionLabel>Failed to sync: {lastAttemptTime}</CaptionLabel>
                    </HorizontalStack>
                    {syncStatus.state === SyncState.FAILING &&
                        syncStatus.lastFailure?.userSafeErrorMessage && (
                            <CaptionLabel color={AXLE_RED}>
                                {syncStatus.lastFailure.userSafeErrorMessage}
                            </CaptionLabel>
                        )}
                </Box>
            </Tooltip>
        );
    } else {
        return (
            <Tooltip
                title={
                    lastSuccess
                        ? `Up-to-date as of ${lastAttemptTimeFull}`
                        : `Not ready to sync as of ${lastAttemptTimeFull}`
                }
            >
                <Box>
                    <HorizontalStack alignItems='center' spacing={1}>
                        {light}
                        <CaptionLabel>
                            Last {resourceExistsInExternalSystem ? 'synced' : 'checked'}:{' '}
                            {lastAttemptTime}
                        </CaptionLabel>
                    </HorizontalStack>
                </Box>
            </Tooltip>
        );
    }
};
